import * as React from "react";
import { uppercaseFirst } from "../../Utilities/Generic/Strings";

export interface CustomWizardElementEvent {
    type: string;
    target: EventTarget;
    currentTarget: EventTarget;
    creationId: number;
}

export interface CustomWizardElementType {
    type: string;
    textNode?: string;
    attributes?: Record<string, any>;
    events?: Array<string>;
    onEvent?: (event: CustomWizardElementEvent) => {
        data: any;
        next?: boolean;
    };
    children?: Array<CustomWizardElementType>;
}

interface Props {
    elementIndex: number;
    element: CustomWizardElementType;
    onEvent: (index: number, event: CustomWizardElementEvent) => void;
    creationId: number;
}

export default class CustomWizardElement extends React.Component<Props> {
    protected handleEvent = (e: React.SyntheticEvent): void => {
        this.props.onEvent(this.props.elementIndex, {
            type         : e.type,
            target       : e.target,
            currentTarget: e.currentTarget,
            creationId   : this.props.creationId,
        });
    };

    protected transformAttributes(): Record<string, any> {
        const props: Record<string, any> = {};

        for(const i in this.props.element.attributes) {
            if(!Object.prototype.hasOwnProperty.call(this.props.element.attributes, i)) {
                continue;
            }

            let propName = i;

            switch(i) {
                case "class":
                    propName = "className";
                    break;
                case "value":
                    propName = "defaultValue";
                    break;
            }

            props[propName] = this.props.element.attributes[i];
        }

        return props;
    }

    protected ensureChildrenValid(type: string): true {
        switch(type) {
            case "input":
                throw new Error(`Element of type '${type}' is not allowed to have children`);
        }

        return true;
    }

    protected determineEvents(): Record<string, (event: React.SyntheticEvent) => void> {
        const events: Record<string, (event: React.SyntheticEvent) => void> = {};

        if(Array.isArray(this.props.element.events)) {
            for(let i = 0, l = this.props.element.events.length; i < l; i++) {
                let eventName = this.props.element.events[i];

                switch(eventName) {
                    case "change":
                    case "click":
                        break;
                    default:
                        throw new Error(`Event of type ${eventName} is not currently supported`);
                }

                eventName = `on${uppercaseFirst(eventName)}`;

                events[eventName] = this.handleEvent;
            }
        }

        return events;
    }

    public render(): React.ReactNode {
        const Type = this.props.element.type;

        if(Type.toLowerCase() !== Type) {
            throw new Error("All custom wizard element types must be lowercase");
        }

        const props = this.transformAttributes();
        const children = [];

        if(typeof this.props.element.textNode === "string") {
            children.push(this.props.element.textNode);
        }

        if(Array.isArray(this.props.element.children)) {
            for(let i = 0, l = this.props.element.children.length; i < l; i++) {
                children.push(
                    <CustomWizardElement
                        key={i}
                        elementIndex={this.props.elementIndex}
                        element={this.props.element.children[i]}
                        onEvent={this.props.onEvent}
                        creationId={this.props.creationId}
                    />
                );
            }
        }

        if(children.length) {
            this.ensureChildrenValid(Type);

            props.children = children;
        }

        const events = this.determineEvents();

        return (
            <Type
                data-testid="wizard-element-custom"
                {...props}
                {...events}
            />
        );
    }
}
